// Canonical checklist definition — matches backend CHECKLIST_SECTIONS keys
export const SECTIONS = [
  {
    id: "seller",
    title: "Seller Information",
    subtitle: "Where we start: the essentials so we can reach you.",
    fields: [
      { key: "seller_name", label: "Seller Name(s)", type: "text", required: true },
      { key: "property_address", label: "Property Address", type: "text", required: true },
      { key: "city_state_zip", label: "City, State and ZIP", type: "text", required: true },
      { key: "cell_phone", label: "Cell Phone", type: "text", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "additional_contact", label: "Additional Seller or Contact", type: "text" },
      {
        key: "preferred_contact_method",
        label: "Preferred Contact Method",
        type: "radio",
        options: ["Phone call", "Text", "Email"],
      },
      {
        key: "best_time_to_contact",
        label: "Best Time to Contact",
        type: "radio",
        options: ["Morning", "Afternoon", "Evening"],
      },
    ],
  },
  {
    id: "goals",
    title: "Selling Goals",
    subtitle: "Your timeline and what a successful sale looks like for you.",
    fields: [
      {
        key: "reason_for_selling",
        label: "Reason for Selling",
        type: "radio",
        options: [
          "Upsizing", "Downsizing", "Relocating", "Retirement",
          "Investment", "Job transfer", "Divorce", "Estate sale", "Other",
        ],
      },
      { key: "other_selling_reason", label: "Other Selling Reason", type: "text" },
      { key: "ideal_list_date", label: "Ideal List Date", type: "date" },
      { key: "ideal_closing_date", label: "Ideal Closing Date", type: "date" },
      {
        key: "rent_back",
        label: "Will You Need a Rent-Back?",
        type: "radio",
        options: ["Yes", "No", "Unsure"],
      },
      {
        key: "purchased_another_home",
        label: "Have You Purchased Another Home?",
        type: "radio",
        options: ["Yes", "No", "Currently looking"],
      },
      {
        key: "preferred_selling_timeline",
        label: "Preferred Selling Timeline",
        type: "radio",
        options: [
          "As soon as possible", "Within 30 days", "Within 60 days",
          "Within 90 days", "3–6 months", "More than 6 months", "Just exploring",
        ],
      },
      {
        key: "top_priorities",
        label: "Top Priorities",
        type: "checkboxes",
        options: [
          "Highest price", "Maximize net proceeds", "Quick sale",
          "Convenience", "Flexible closing", "Certainty of closing",
        ],
      },
    ],
  },
  {
    id: "property",
    title: "Property Overview",
    subtitle: "The high-level picture of your home.",
    fields: [
      {
        key: "property_type",
        label: "Property Type",
        type: "radio",
        options: ["Single-family home", "Condominium", "Townhome", "Duplex", "Other"],
      },
      { key: "other_property_type", label: "Other Property Type", type: "text" },
      {
        key: "has_hoa",
        label: "Is There an HOA?",
        type: "radio",
        options: ["Yes", "No", "Unsure"],
      },
      { key: "monthly_hoa_fee", label: "HOA Fee Amount", type: "currency" },
      {
        key: "hoa_fee_frequency",
        label: "HOA Fee Frequency",
        type: "radio",
        options: ["Monthly", "Quarterly", "Semi-annually", "Annually", "Other"],
      },
      {
        key: "hoa_includes",
        label: "What Does the HOA Include?",
        type: "checkboxes",
        options: [
          "Pool", "Clubhouse", "Gym", "Tennis or pickleball", "Water",
          "Sewer", "Garbage", "Insurance", "Roof", "Exterior maintenance",
          "Landscaping", "Security", "Gated community", "Cable or internet",
        ],
      },
      { key: "other_hoa_info", label: "Other HOA Information", type: "text" },
      {
        key: "home_facing_direction",
        label: "Home Facing Direction",
        type: "radio",
        options: [
          "North",
          "Northeast",
          "East",
          "Southeast",
          "South",
          "Southwest",
          "West",
          "Northwest",
          "Unsure",
        ],
      },
      {
        key: "parking",
        label: "Parking",
        type: "checkboxes",
        options: [
          "1-car garage", "2-car garage", "3+ car garage", "Detached garage",
          "Carport", "Assigned parking", "Covered parking", "Guest parking",
          "Street parking", "EV charger", "RV or boat parking", "None",
        ],
      },
      {
        key: "parking_space_count",
        label: "Number of Assigned Parking Spaces (condo/townhome)",
        type: "number",
      },
      {
        key: "parking_space_numbers",
        label: "Assigned Parking Space Number(s)",
        type: "text",
        placeholder: "e.g., #142, #143",
      },
      {
        key: "storage_unit_number",
        label: "Assigned Storage Unit Number (if any)",
        type: "text",
      },
    ],
  },
  {
    id: "features",
    title: "Home Features and Upgrades",
    subtitle: "The details that make your home shine.",
    fields: [
      {
        key: "kitchen_features",
        label: "Kitchen Features",
        type: "checkboxes",
        options: ["Updated", "Quartz", "Granite", "Island", "Pantry"],
      },
      {
        key: "bathroom_features",
        label: "Bathroom Features",
        type: "checkboxes",
        options: ["Updated", "Walk-in shower", "Soaking tub", "Double vanity"],
      },
      {
        key: "flooring",
        label: "Flooring",
        type: "checkboxes",
        options: ["Hardwood", "Luxury vinyl plank", "Tile", "Carpet", "Laminate"],
      },
      {
        key: "home_features",
        label: "Home Features",
        type: "checkboxes",
        options: [
          "Solar", "Smart home", "Dual-pane windows", "Fireplace", "Skylights",
          "Whole-house fan", "Central A/C", "Recessed lighting", "Water softener",
          "Security system", "EV charger", "Generator", "Crown molding", "Vaulted ceilings",
        ],
      },
      {
        key: "outdoor_features",
        label: "Outdoor Features",
        type: "checkboxes",
        options: [
          "Pool", "Spa", "Deck", "Patio", "Outdoor kitchen", "Fire pit",
          "Garden", "Fruit trees", "Fenced yard", "Irrigation",
        ],
      },
      {
        key: "other_features",
        label: "Other Features or Special Materials",
        type: "textarea",
        placeholder: "Custom materials, upgrades, views, etc.",
      },
      { key: "upgrade_dates", label: "Approximate Dates of Major Upgrades", type: "textarea" },
      { key: "love_most", label: "What Do You Love Most About the Home?", type: "text" },
    ],
  },
  {
    id: "appliances",
    title: "Appliances and Items Included",
    subtitle: "What conveys with the home.",
    fields: [
      {
        key: "appliances_included",
        label: "Appliances Included in the Sale",
        type: "checkboxes",
        options: [
          "Refrigerator", "Range or cooktop", "Oven", "Microwave",
          "Dishwasher", "Washer", "Dryer", "Wine refrigerator",
          "Garage refrigerator", "Water softener",
        ],
      },
      {
        key: "other_items_included",
        label: "Other Items Included",
        type: "checkboxes",
        options: [
          "Window coverings", "Ceiling fans", "TV mounts", "Security cameras",
          "Smart doorbell", "Storage cabinets", "BBQ", "Patio furniture", "Hot tub",
        ],
      },
      { key: "other_items_extra", label: "Other Items Included (Additional field)", type: "text" },
      {
        key: "laundry_hookup",
        label: "Laundry Hookup",
        type: "radio",
        options: [
          "220V electric only",
          "Gas only",
          "Both 220V electric and gas",
          "110V only",
          "Unsure",
        ],
      },
      {
        key: "range_oven_hookup",
        label: "Range / Oven Hookup",
        type: "radio",
        options: [
          "Gas",
          "Electric (220V)",
          "Electric (110V)",
          "Induction",
          "Dual fuel",
          "Unsure",
        ],
      },
      {
        key: "other_220v_outlets",
        label: "Other 220V Outlets",
        type: "checkboxes",
        options: [
          "Garage",
          "Workshop",
          "EV charger circuit",
          "Kitchen (double oven)",
          "None",
        ],
      },
      { key: "items_excluded", label: "Items Excluded From the Sale", type: "textarea" },
    ],
  },
  {
    id: "access",
    title: "Access and Security Information",
    subtitle: "So we can safely show and hand off the home.",
    fields: [
      {
        key: "keys_available",
        label: "Keys and Access Items Available",
        type: "checkboxes",
        options: [
          "Front door key", "Back door key", "Mailbox key", "Side gate key",
          "Garage door remotes", "Gate remote or fob", "Pool key or fob",
          "Clubhouse key or fob", "Storage unit key",
        ],
      },
      { key: "garage_remote_count", label: "Number of Garage Remotes", type: "number" },
      { key: "other_keys", label: "Other Keys, Remotes or Fobs", type: "text" },
      { key: "front_door_code", label: "Front Door Code", type: "text" },
      { key: "garage_code", label: "Garage Code", type: "text" },
      { key: "gate_code", label: "Gate Code", type: "text" },
      { key: "alarm_code", label: "Alarm Code", type: "text" },
      { key: "other_access_codes", label: "Other Access Codes or Instructions", type: "text" },
      {
        key: "showing_instructions",
        label: "Preferred Showing Instructions",
        type: "radio",
        options: [
          "Supra lockbox", "Combination lockbox", "Appointment required",
          "Call first", "Text first",
        ],
      },
      {
        key: "showing_restrictions",
        label: "Showing Restrictions or Special Instructions",
        type: "textarea",
      },
    ],
  },
  {
    id: "condition",
    title: "Property Condition and Disclosures",
    subtitle: "Full transparency protects you and buyers.",
    fields: [
      {
        key: "known_issues",
        label: "Known Issues or Conditions",
        type: "checkboxes",
        options: [
          "Roof", "Plumbing", "Electrical", "Foundation", "Water damage",
          "Insurance claims", "Termite or pest", "Neighbor disputes",
          "Easements", "Permit issues", "None known",
        ],
      },
      {
        key: "additional_condition_details",
        label: "Additional Details",
        type: "textarea",
        placeholder: "Repairs, leaks, disputes, etc.",
      },
      { key: "inspection_reports", label: "Existing Inspection Reports or Warranties", type: "text" },
      { key: "permits_receipts", label: "Permits, Receipts and Improvement Records", type: "text" },
    ],
  },
  {
    id: "utilities",
    title: "Utilities and Services",
    subtitle: "Who provides what — helpful for buyers and closing.",
    fields: [
      { key: "electric_provider", label: "Electric Provider", type: "text" },
      { key: "gas_provider", label: "Gas Provider", type: "text" },
      { key: "water_provider", label: "Water Provider", type: "text" },
      { key: "garbage_provider", label: "Garbage Provider", type: "text" },
      { key: "internet_provider", label: "Internet Provider", type: "text" },
      { key: "other_utility_provider", label: "Other Utility or Service Provider", type: "text" },
      {
        key: "owner_paid_utilities",
        label: "Utilities Paid Directly by the Owner",
        type: "checkboxes",
        options: ["Electricity", "Gas", "Water", "Sewer", "Garbage", "Internet"],
      },
      { key: "utility_notes", label: "Utility, Solar or Service Notes", type: "textarea" },
    ],
  },
  {
    id: "marketing",
    title: "Marketing and Presentation",
    subtitle: "How we'll bring the world to your front door.",
    fields: [
      {
        key: "preparation_openness",
        label: "Are You Open to Any of These Preparations?",
        type: "checkboxes",
        options: [
          "Staging", "Painting", "Minor repairs", "Landscaping",
          "Decluttering", "Storage", "Professional cleaning",
        ],
      },
      {
        key: "marketing_preferences",
        label: "Marketing Preferences",
        type: "checkboxes",
        options: [
          "Professional photography", "Video", "Drone", "3D tour",
          "Floor plan", "Open houses", "Coming soon", "Social media advertising",
          "Broker tour", "Property website", "Mailers",
        ],
      },
      { key: "marketing_requests", label: "Marketing Requests or Concerns", type: "textarea" },
      { key: "additional_info", label: "Additional Information or Questions", type: "text" },
      {
        key: "consent",
        label:
          "I authorize Equitas Homes and Louis Fong to contact me regarding my property and potential real estate services.",
        type: "consent",
        required: true,
      },
    ],
  },
];

export const ALL_FIELDS_MAP = SECTIONS.reduce((acc, s) => {
  s.fields.forEach((f) => (acc[f.key] = { ...f, section: s.title }));
  return acc;
}, {});
